import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Save, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { getSlotsForDate } from "@/lib/hours";
import api from "@/lib/api";

// type: "grooming" | "selfwash"
export const EditBookingDialog = ({ open, onOpenChange, item, type, onSaved }) => {
  const { t } = useLang();
  const a = t.admin;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      if (type === "grooming") {
        setForm({
          customer_name: item.customer_name || "",
          phone: item.phone || "",
          pet_name: item.pet_name || "",
          service: item.service || "",
          preferred_date: item.preferred_date || "",
          preferred_time: item.preferred_time || "",
          notes: item.notes || "",
        });
      } else {
        setForm({
          customer_name: item.customer_name || "",
          phone: item.phone || "",
          nagayu: !!item.nagayu,
          date: item.date || "",
          time: item.time || "",
          notes: item.notes || "",
        });
      }
    }
  }, [open, item, type]);

  if (!item) return null;

  const dateKey = type === "grooming" ? "preferred_date" : "date";
  const timeKey = type === "grooming" ? "preferred_time" : "time";
  const dateVal = form[dateKey] || "";

  // Compute available slots for chosen date (fallback to current stored slot if outside window)
  const slots = dateVal ? getSlotsForDate(new Date(dateVal + "T00:00:00")) : [];
  const currentSlot = form[timeKey];
  const slotOptions = currentSlot && !slots.includes(currentSlot) ? [currentSlot, ...slots] : slots;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const url = type === "grooming"
        ? `/admin/grooming-requests/${item.id}`
        : `/admin/selfwash-bookings/${item.id}`;
      const payload = { ...form };
      // Remove empty strings so they don't overwrite existing values with ""
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      // Ask if we should notify the customer via email/SMS (if we have any contact channel)
      if (item.email || item.phone) {
        payload.notify_customer = window.confirm(a.notify_customer_question);
      }
      const res = await api.patch(url, payload);
      toast.success(payload.notify_customer ? a.saved_with_email : a.saved);
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.status === 409 ? t.selfwash.booking.slot_taken : a.save_error;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const field = "rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-cream max-w-lg rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="edit-booking-dialog">
        <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
              <Pencil className="h-5 w-5" /> {a.edit_title}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/80">
              {type === "grooming" ? a.grooming_type : a.selfwash_type} · {item.customer_name}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-brand-ink font-semibold">{t.grooming.customer_name}</Label>
              <Input data-testid="edit-name" value={form.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} className={field} />
            </div>
            <div>
              <Label className="text-brand-ink font-semibold">{t.grooming.phone}</Label>
              <Input data-testid="edit-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={field} />
            </div>
          </div>

          {type === "grooming" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-brand-ink font-semibold">{t.grooming.pet_name}</Label>
                <Input data-testid="edit-petname" value={form.pet_name || ""} onChange={(e) => set("pet_name", e.target.value)} className={field} />
              </div>
              <div>
                <Label className="text-brand-ink font-semibold">{t.grooming.service_wanted}</Label>
                <Input data-testid="edit-service" value={form.service || ""} onChange={(e) => set("service", e.target.value)} className={field} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-brand-ink font-semibold">{t.grooming.date}</Label>
              <Input
                data-testid="edit-date"
                type="date"
                value={dateVal}
                onChange={(e) => set(dateKey, e.target.value)}
                className={field}
              />
            </div>
            <div>
              <Label className="text-brand-ink font-semibold">{t.grooming.time}</Label>
              <Select value={form[timeKey] || ""} onValueChange={(v) => set(timeKey, v)}>
                <SelectTrigger data-testid="edit-time" className={field}><SelectValue placeholder="--:--" /></SelectTrigger>
                <SelectContent>
                  {slotOptions.length === 0 && <div className="px-3 py-2 text-sm text-brand-ink/50">{t.grooming.closed_day}</div>}
                  {slotOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {type === "selfwash" && (
            <button
              type="button"
              onClick={() => set("nagayu", !form.nagayu)}
              className={`w-full text-left rounded-xl border p-3 flex items-center justify-between transition-colors ${
                form.nagayu ? "border-bamboo bg-bamboo-soft" : "border-brand/15 bg-white"
              }`}
              data-testid="edit-nagayu"
            >
              <span className="text-sm font-bold text-brand-ink">Nagayu CO₂ Spa (+10€)</span>
              <Switch checked={!!form.nagayu} onCheckedChange={(v) => set("nagayu", v)} onClick={(e) => e.stopPropagation()} />
            </button>
          )}

          <div>
            <Label className="text-brand-ink font-semibold">{t.grooming.notes}</Label>
            <Textarea
              data-testid="edit-notes"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand min-h-[90px]"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              data-testid="edit-cancel-btn"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full border-brand/30 text-brand font-bold h-11 px-6"
            >
              {a.cancel}
            </Button>
            <Button
              data-testid="edit-save-btn"
              onClick={save}
              disabled={saving}
              className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11"
            >
              <Save className="h-4 w-4 mr-2" /> {saving ? a.saving : a.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditBookingDialog;
